import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import { api } from './lib/api';
import { bridge } from './lib/bridge';
import { initSentry, captureException } from './lib/sentry';
import { ForceUpdate, UpdateRecommendedToast } from './components/ForceUpdate';
import type { ApiVersion } from '@butler/shared';
import './index.css';

initSentry();

window.addEventListener('error', (e) => captureException(e.error, { type: 'window.error' }));
window.addEventListener('unhandledrejection', (e) =>
  captureException(e.reason, { type: 'unhandledrejection' }),
);

const queryClient = new QueryClient({
  defaultOptions: {
    // WebView는 focus/visibility 이벤트를 빈번히 발생시킬 수 있어 refetchOnWindowFocus를 끔
    // (서버 도달 불가 시 me 쿼리가 초당 수십 회 재요청되는 루프 관찰됨).
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false, refetchOnReconnect: false },
  },
});

type VersionState =
  | { kind: 'loading' }
  | { kind: 'ok' }
  | { kind: 'force-update'; remote: ApiVersion; current: number }
  | { kind: 'recommend-update'; remote: ApiVersion; current: number };

function Root() {
  const [state, setState] = useState<VersionState>({ kind: 'loading' });
  const [toastDismissed, setToastDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Kotlin bridge가 없는 dev 환경에서도 동작해야 함 — fallback 1
        const local = bridge().getAppVersion();
        const remote = await api.getVersion();
        if (cancelled) return;
        if (local.versionCode < remote.min_supported_version_code) {
          setState({ kind: 'force-update', remote, current: local.versionCode });
        } else if (local.versionCode < remote.latest_version_code) {
          setState({ kind: 'recommend-update', remote, current: local.versionCode });
        } else {
          setState({ kind: 'ok' });
        }
      } catch (err) {
        // 네트워크 실패: 그냥 통과 (오프라인이라도 앱을 못 쓰게 막진 않음)
        console.warn('[boot] version check failed', err);
        captureException(err, { phase: 'version-check' });
        if (!cancelled) setState({ kind: 'ok' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <div className="text-[14px] text-[var(--text-muted)]">Butler 시작 중…</div>
      </div>
    );
  }

  if (state.kind === 'force-update') {
    return (
      <ForceUpdate
        apkUrl={state.remote.apk_url}
        currentVersionCode={state.current}
        minSupportedVersionCode={state.remote.min_supported_version_code}
      />
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {state.kind === 'recommend-update' && !toastDismissed && (
        <UpdateRecommendedToast
          apkUrl={state.remote.apk_url}
          onDismiss={() => setToastDismissed(true)}
        />
      )}
    </QueryClientProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
